import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, FileText, UserCircle, FolderArchive, FolderOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = { clients: UserCircle, cases: FolderArchive, contracts: FileText, documents: FolderArchive, templates: FolderOpen };
const labelMap = { clients: "Clients", cases: "Dossiers", contracts: "Contrats", documents: "Documents", templates: "Modèles" };
const linkMap = { clients: "/clients", cases: "/cases", contracts: "/contracts", documents: "/documents", templates: "/templates" };

function ResultItem({ type, item }) {
  var navigate = useNavigate();
  var Icon = iconMap[type] || FileText;
  var name = item.name || item.title || item.original_filename || item.reference || "Sans titre";
  var sub = item.email || item.status || item.category || item.description || "";
  var link = linkMap[type] || "/dashboard";

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-200 cursor-pointer bg-white transition-colors" onClick={function() { navigate(link); }} data-testid={"result-" + (item.client_id || item.case_id || item.contract_id || item.document_id || item.template_id)}>
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{name}</p>
        {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
      </div>
      <Badge variant="secondary" className="text-xs">{labelMap[type]}</Badge>
    </div>
  );
}

export default function SearchPage({ user, setUser }) {
  var [query, setQuery] = useState("");
  var [results, setResults] = useState(null);
  var [searching, setSearching] = useState(false);

  async function handleSearch(q) {
    setQuery(q);
    if (q.length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      var res = await fetch(API_URL + "/api/search?q=" + encodeURIComponent(q), { credentials: "include" });
      if (res.ok) setResults(await res.json());
    } catch (e) { toast.error("Erreur"); }
    setSearching(false);
  }

  var categories = results ? Object.keys(results.results || {}) : [];

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8 max-w-3xl mx-auto" data-testid="search-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Recherche</h1>
          <p className="text-sm text-muted-foreground">Recherchez dans vos clients, dossiers, contrats et documents</p>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={query}
            onChange={function(e) { handleSearch(e.target.value); }}
            placeholder="Tapez pour rechercher..."
            className="pl-12 h-12 text-base"
            autoFocus
            data-testid="global-search-input"
          />
          {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />}
        </div>

        {results && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">{results.total} résultat{results.total !== 1 ? "s" : ""} pour "{results.query}"</p>
            {results.total === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">Aucun résultat trouvé</p>
                </CardContent>
              </Card>
            ) : categories.map(function(cat) {
              var items = results.results[cat];
              if (!items || items.length === 0) return null;
              return (
                <div key={cat}>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">{labelMap[cat] || cat} <Badge variant="secondary">{items.length}</Badge></h3>
                  <div className="space-y-2">
                    {items.map(function(item, i) { return <ResultItem key={i} type={cat} item={item} />; })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
